import React from 'react';
import { connect, MapStateToProps } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';

import { SpaceBetween } from 'utils/style/extendable';
import { appDispatch, RootState } from 'store/store';
import { updateItem, requireProviderTags } from 'store/providers/providerSlice';
import { IOption } from 'components/Select/Select';

import {
  CollectionItem,
  CollectionContent,
  ItemAvatar,
  AddTag,
} from './styles';
import Chip from 'components/Chip';
import Icon from 'components/Icon';
import Button from 'components/Button';
import Select from 'components/Select';
import { display } from 'components/ErrorView/ErrorDialog';
import {
  IProviderItemViewWithData,
  ITagView,
} from 'store/providers/providerTypes';
import LocalIcon, { LocalIconTypes } from '../../../../ui/LocalIcon';
import i18n, { languageDirection } from '../../../../i18n';
import CKEditorField from '../../../../components/CKEditorField/CKEditorField';
import Input from '../../../../components/Input';

interface IOwnProps {
  item: IProviderItemViewWithData;
  onComplete: () => void;
  icon: string;
  color: string;
}

interface IStateProps {
  tags: ITagView[];
  providerId: number;
}

type IComponentProps = IOwnProps & IStateProps;

interface IComponentStates {
  tags: IOption[];
  name: string;
  formatedName: string;
  saving: boolean;
  editingTags: boolean;
  editingOrder: boolean;
  order: number;
}

const CKEditorConfig = {
  toolbar: {
    items: ['fontColor'],
  },
  language: i18n.language,
};

class RenameView extends React.Component<IComponentProps, IComponentStates> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      name: props.item.data.name || '',
      formatedName: props.item.data.formatedName || '',
      saving: false,
      editingTags: false,
      tags: props.item.data.tags?.map((tag) => ({
        value: tag.id,
        label: tag.text,
      })),
      editingOrder: false,
      order: props.item.data.order,
    };
  }

  componentDidMount() {
    appDispatch(
      requireProviderTags({ providerId: this.props.item.data.providerId })
    ).catch((err: any) => {
      display(err);
    });
  }

  handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    this.setState({ name: value });
  };

  handleChangeName = (formatedName: string) => {
    const parser = new DOMParser();
    const parsedName = parser.parseFromString(formatedName, 'text/html');
    const name = parsedName.body.innerText;
    this.setState({
      name,
      formatedName,
    });
  };

  handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const item = cloneDeep(this.props.item.data);
    // update item name
    item.name = this.state.name;
    item.formatedName = this.state.formatedName;
    item.order = this.state.order;
    if (this.state.editingTags) {
      item.tags = this.state.tags.map(
        (tagOption) =>
          ({ text: tagOption.label, id: tagOption.value }) as ITagView
      );
    }
    this.setState({ saving: true });
    appDispatch(updateItem({ item }) as any).then(
      () => {
        this.setState({ saving: false });
        this.props.onComplete();
      },
      (err: any) => {
        display(err);
      }
    );
  };

  handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.props.onComplete();
  };

  handleEditTags = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({ editingTags: true });
  };
  handleEditOrder = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({ editingOrder: true });
  };

  handleChangeTags = (values: IOption[]) => {
    this.setState({ tags: values });
  };

  render() {
    return (
      <CollectionItem key={this.props.item.data.id}>
        <CollectionContent>
          <ItemAvatar>
            <LocalIcon
              iconName={this.props.icon as LocalIconTypes}
              color={this.props.color}
              type="lg"
            ></LocalIcon>
          </ItemAvatar>
          <div style={{ flex: '1' }}>
            <form onSubmit={this.handleSubmit} style={{ width: '100%' }}>
              <CKEditorField
                label={i18n.t('nameTitle', { ns: 'documentManager' }) as string}
                onChange={(_: any, editor: any) => {
                  const data = editor.getData();
                  this.handleChangeName(data);
                }}
                data={this.state.formatedName || this.state.name}
                dir={`${languageDirection()}`}
                config={CKEditorConfig}
              />
              <div style={{ margin: '1em 0' }}>
                {this.state.editingTags ? (
                  <Select
                    options={this.props.tags
                      .filter((item) => !item.deleted)
                      .map((tag) => ({
                        value: tag.id,
                        label: tag.text,
                      }))}
                    values={this.state.tags}
                    onChangeValues={this.handleChangeTags}
                    label={
                      i18n.t('addTag', { ns: 'documentManager' }) as string
                    }
                    multiChoice={true}
                  />
                ) : (
                  <React.Fragment>
                    {this.state.tags?.map((tag) => (
                      <Chip key={tag.value} chip={tag} selected={false} />
                    ))}
                    {this.state.tags?.length < 1 ? (
                      <AddTag href="#!" onClick={this.handleEditTags}>
                        <Icon type={'plus-square'} />
                        {i18n.t('addTag', { ns: 'documentManager' })}
                      </AddTag>
                    ) : (
                      <a
                        href="#!"
                        className="teal-text text-lighten-1"
                        onClick={this.handleEditTags}
                      >
                        <Icon type={'edit-2'} />
                      </a>
                    )}
                  </React.Fragment>
                )}
              </div>
              <div>
                {this.state.editingOrder ? (
                  <Input
                    id={'changeOrder' + this.props.item.data.id}
                    type={'text'}
                    label={i18n.t('order', { ns: 'documentManager' }) as string}
                    value={this.state.order}
                    onChange={(e) =>
                      this.setState({ order: Number(e.target.value) })
                    }
                  />
                ) : (
                  <span>
                    {`${i18n.t('order', { ns: 'documentManager' })} : `}
                    {this.state.order}{' '}
                    <a
                      href="#!"
                      className="teal-text text-lighten-1"
                      onClick={this.handleEditOrder}
                    >
                      <Icon type={'edit-2'} />
                    </a>{' '}
                  </span>
                )}
              </div>
              <SpaceBetween>
                <Button type="submit" color="green" loading={this.state.saving}>
                  {i18n.t('confirm', { ns: 'documentManager' }) as string}
                </Button>
                <Button onClick={this.handleCancel} color="red">
                  {i18n.t('cancel', { ns: 'documentManager' }) as string}
                </Button>
              </SpaceBetween>
            </form>
          </div>
        </CollectionContent>
      </CollectionItem>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const providerId = props.item.data.providerId;
  const tags = state.providers?.tags[providerId]?.data
    ? Object.values(state.providers!.tags[providerId!]!.data)
    : ([] as ITagView[]);

  return { providerId, tags };
};

export default connect(mapStateToProps)(RenameView);
